import { SUPPLIER_ID } from 'constants/supplier';
import { getBidTeamCompositeKeyAsString } from 'util/bidTeam';

//

export const transformToCuratedBidTeams = (bidTeams, supplierVendorMappings = []) => {
  if (bidTeams.length === 0) {
    return [];
  }

  const eipCompanyKeys = getBidTeamMemberCompanyKeys(bidTeams);

  const eipCompanyKeyToSupplierMap = {};
  for (const eipCompanyKey of eipCompanyKeys) {
    eipCompanyKeyToSupplierMap[eipCompanyKey] = _findSupplierByEipCompanyKey(supplierVendorMappings, eipCompanyKey);
  }

  const convertedBidTeams = [];
  for (const bidTeam of bidTeams) {
    const convertedBidTeam = _convertBidTeam(bidTeam);

    for (const btm of bidTeam.bidTeamMembers) {
      const btmCompanyKey = btm.memberCompany.companyKey;

      let btmSupplier = eipCompanyKeyToSupplierMap[btmCompanyKey];
      btmSupplier.bidTeamMemberCompanyKey = btmCompanyKey;

      if (btmSupplier.id === -1) {
        // The "supplier id" here starts with "EIP_" to denote that this unfamilied vendor is being
        // stored using its EIP company key (it doesn't have a unique supplier id)
        btmSupplier = {
          bidTeamMemberCompanyKey: btmCompanyKey,
          id: `${SUPPLIER_ID.EIP_COMPANY_PREFIX}${btm.memberCompany.companyKey}`,
          name: btm.memberCompany.companyName
        };
      }
      _addSupplierIfNotExists(convertedBidTeam, btmSupplier);

      const sharedSupplier = convertedBidTeam.suppliers[btmSupplier.id];
      for (const person of btm.supplierUsers) {
        // Keep a map of personKeys in the bid team to easily be able to check whether a person is in the bid team.
        convertedBidTeam.bidTeamPersonKeys.push(person.personKey);
        _addPersonToSharedSupplierPersonList(
          person,
          bidTeam,
          sharedSupplier,
          btm.compositeKey.offeringKey,
          btm.memberCompany.companyName
        );
      }
    }
    convertedBidTeam.bidTeamMemberKeys = bidTeam.bidTeamMembers.map(member => member.memberCompany.companyKey);
    convertedBidTeams.push(convertedBidTeam);
  }

  return convertedBidTeams;
};

//

export function getBidTeamMemberCompanyKeys(bidTeams) {
  if (!Array.isArray(bidTeams) || !bidTeams.length) {
    return [];
  }

  return bidTeams.reduce((companyKeyAccum, currentBidTeam) => {
    return Array.isArray(currentBidTeam.bidTeamMembers) && currentBidTeam.bidTeamMembers.length
      ? companyKeyAccum.concat(
          currentBidTeam.bidTeamMembers.map(bidTeamMember => bidTeamMember.memberCompany.companyKey)
        )
      : companyKeyAccum;
  }, []);
}

//

function _findSupplierByEipCompanyKey(supplierVendorMappings, eipCompanyKey) {
  for (const supplierEipCompanies of supplierVendorMappings) {
    for (const eipCompany of supplierEipCompanies.vendors) {
      if (eipCompany.eip_companykey === eipCompanyKey && supplierEipCompanies.supplier.id !== 'S_0') {
        return supplierEipCompanies.supplier;
      }
    }
  }

  return { id: -1 };
}

//

function _convertBidTeam(originalBidTeam) {
  return {
    bidIntention: originalBidTeam.bidIntention,
    bidStatus: originalBidTeam.bidStatus,
    // list for checking whether a person is in the bid team.
    bidTeamPersonKeys: [],
    compositeKey: originalBidTeam.compositeKey,
    id: getBidTeamCompositeKeyAsString(originalBidTeam),
    name: originalBidTeam.teamName,
    suppliers: {}
  };
}

//

function _addSupplierIfNotExists(bidTeam, supplier) {
  const alreadySeenSupplier = bidTeam.suppliers[supplier.id];
  if (!alreadySeenSupplier) {
    bidTeam.suppliers[supplier.id] = {
      people: {},
      ...supplier
    };
  }
}

//

function _addPersonToSharedSupplierPersonList(person, bidTeam, sharedSupplier, offeringKey, companyName) {
  const convertedPerson = _convertPerson(person, bidTeam, sharedSupplier, offeringKey, companyName);
  sharedSupplier.people[convertedPerson.personKey] = convertedPerson;
}

//

function _convertPerson(originalPerson, bidTeam, btmSupplier, offeringKey, companyName) {
  const personKey = originalPerson.personKey;

  return {
    companyName,
    eipCompanyKey: originalPerson.companyKey,
    email: originalPerson.emailAddress,
    firstName: originalPerson.firstName,
    invitedTime: originalPerson.fieldMap.invitedTime,
    isMainContact: bidTeam.fieldMap.mainContact === personKey,
    lastLoginSuccessTimestamp: originalPerson.lastLoginSuccess,
    lastName: originalPerson.lastName,
    lastViewTime: originalPerson.fieldMap.lastViewTime,
    name: `${originalPerson.firstName} ${originalPerson.lastName}`,
    offeringKey,
    personKey,
    personStatusCode: originalPerson.personStatusCode,
    supplierId: btmSupplier.id,
    username: originalPerson.username,
    workPhone: originalPerson.workPhone
  };
}
